import React, { useRef, useState } from "react";
import {
  UNEXPECTED_RESPONSE_MESSAGE,
  normalizeResponseErrors,
  parseJsonResponseBody,
} from "../utils/apiErrors";
import { csrfToken } from "../utils/csrf";

const ATTACHMENT_ACCEPT = "image/*,.pdf,text/plain,text/csv";

/**
 * Reply composer pinned to the bottom of the ticket drawer. Posts a new
 * ticket message (with optional file attachments) and hands the created
 * record back to the parent on success.
 *
 * @param {{ ticketId: number | string, onMessageCreated: (message: unknown) => void }} props
 */
export default function TicketReplyForm({ ticketId, onMessageCreated }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const resetFiles = () => {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    setErrors([]);
    try {
      const formData = new FormData();
      formData.append("ticket_message[body]", trimmed);
      files.forEach((file) => {
        formData.append("ticket_message[attachments][]", file);
      });

      const res = await fetch(`/tickets/${ticketId}/ticket_messages.json`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken() ?? "",
        },
        body: formData,
      });
      const parsed = parseJsonResponseBody(await res.text());
      if (!parsed.ok) {
        setErrors([UNEXPECTED_RESPONSE_MESSAGE]);
        return;
      }
      if (!res.ok) {
        setErrors(
          normalizeResponseErrors(parsed.data, "Could not send reply."),
        );
        return;
      }
      onMessageCreated(parsed.data);
      setBody("");
      resetFiles();
    } catch {
      setErrors(["Network error. Try again."]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="shrink-0 border-t border-[var(--color-border)] px-6 py-4"
      onSubmit={handleSubmit}
    >
      {errors.length > 0 ? (
        <div className="form-errors mb-3" role="alert">
          <ul>
            {errors.map((msg, index) => (
              <li key={`${index}-${msg}`}>{msg}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <label htmlFor="ticket-reply-body" className="sr-only">
        Reply
      </label>
      <textarea
        id="ticket-reply-body"
        className="w-full resize-y rounded border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm"
        rows={3}
        placeholder="Compose reply…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={submitting}
      />

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <label htmlFor="ticket-reply-attachments" className="sr-only">
            Attachments
          </label>
          <input
            id="ticket-reply-attachments"
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            className="text-xs text-muted file:mr-2 file:rounded file:border file:border-[var(--color-border)] file:bg-transparent file:px-2 file:py-1 file:text-xs"
            onChange={(e) => setFiles(Array.from(e.target.files))}
            disabled={submitting}
          />
          {files.length > 0 ? (
            <p className="mt-1 truncate text-xs text-muted">
              {files.map((file) => file.name).join(", ")}
            </p>
          ) : null}
        </div>
        <button
          type="submit"
          className="btn btn--primary shrink-0"
          disabled={submitting || body.trim().length === 0}
        >
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
}
